import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Database, Layers, ShieldAlert, Building2, ArrowRight } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { StatCard, SectionTitle } from "@/components/shared";
import { adminApi } from "@/lib/apiClient";
import { moduleMeta, CHART_COLORS } from "@/lib/icons";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, CartesianGrid } from "recharts";

export default function OverviewTab({ modules }) {
  const [data, setData] = useState(null);
  const nameOf = (k) => (modules.find((m) => m.key === k)?.name || k);

  useEffect(() => {
    adminApi.get("/admin/overview").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return <div className="text-sm text-muted-foreground">Loading overview...</div>;

  const moduleData = Object.entries(data.per_module || {})
    .map(([k, v]) => ({ key: k, name: nameOf(k), value: v }))
    .sort((a, b) => b.value - a.value);
  const redData = Object.entries(data.redaction_by_type || {})
    .map(([k, v]) => ({ name: k, value: v }))
    .sort((a, b) => b.value - a.value);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard testid="stat-indexed" label="Records Indexed" value={data.indexed_records.toLocaleString()} icon={Database} hint="Embeddings on server" />
        <StatCard testid="stat-modules" label="Modules Covered" value={`${data.modules_covered}/${data.total_modules}`} icon={Layers} accent="text-blue-300" />
        <StatCard testid="stat-redactions" label="Params Redacted" value={data.redactions.toLocaleString()} icon={ShieldAlert} accent="text-rose-300" hint="Zero-exposure sanitization" />
        <StatCard testid="stat-tenants" label="Active Tenants" value={data.tenants} icon={Building2} accent="text-emerald-300" />
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <Card className="p-5 bg-card border-border lg:col-span-2">
          <SectionTitle>Records per Module</SectionTitle>
          <div className="mt-4 h-72" data-testid="module-coverage-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={moduleData} layout="vertical" margin={{ left: 20, right: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" horizontal={false} />
                <XAxis type="number" stroke="hsl(var(--muted-foreground))" fontSize={11} />
                <YAxis type="category" dataKey="name" width={150} stroke="hsl(var(--muted-foreground))" fontSize={11} />
                <Tooltip
                  contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 12, fontSize: 12 }}
                  cursor={{ fill: "hsl(var(--accent))", opacity: 0.3 }}
                />
                <Bar dataKey="value" radius={[0, 6, 6, 0]}>
                  {moduleData.map((d, i) => (
                    <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-5 bg-card border-border">
          <SectionTitle>Redactions by Type</SectionTitle>
          <div className="mt-4 space-y-2.5" data-testid="redaction-by-type">
            {redData.length === 0 && <div className="text-sm text-muted-foreground">No redactions yet.</div>}
            {redData.map((r) => {
              const max = redData[0].value || 1;
              return (
                <div key={r.name}>
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="font-mono text-muted-foreground">{r.name}</span>
                    <span className="tabular-nums">{r.value.toLocaleString()}</span>
                  </div>
                  <div className="h-2 rounded-full bg-muted overflow-hidden">
                    <div className="h-full rounded-full bg-rose-400/70" style={{ width: `${(r.value / max) * 100}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
          <Link to="/security">
            <Button variant="secondary" className="w-full mt-5" data-testid="go-security-button">
              View Security Overview <ArrowRight className="h-4 w-4" />
            </Button>
          </Link>
        </Card>
      </div>
    </div>
  );
}
